import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Search, 
  ArrowLeft, 
  Utensils, 
  Sparkles, 
  Clock, 
  ChevronRight, 
  ChevronLeft, 
  X, 
  Share2, 
  Info,
  Facebook,
  Instagram,
  Linkedin,
  Twitter
} from "lucide-react";
import localLogo from "../assets/logo.png";
import ProgressiveImage from "./ProgressiveImage";
import { getApiBaseUrl, getMediaBaseUrl } from "../utils/env";
import { formatCurrency } from "../utils/currency";

const ITEM_PLACEHOLDER = "https://placehold.co/600x450/0f5132/ffffff?text=Pomma+Dining";

export default function EMenu() {
  const navigate = useNavigate();

  // State
  const [foodItems, setFoodItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [resortInfo, setResortInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [availableOnly, setAvailableOnly] = useState(false);

  // Modal & Detail view
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [copiedLink, setCopiedLink] = useState(false);

  // Category scroll ref
  const categoryScrollRef = useRef(null);

  // Fetch menu data
  useEffect(() => {
    let isMounted = true;
    const fetchMenuData = async () => {
      setIsLoading(true);
      setError(null);
      const apiBase = getApiBaseUrl();

      try {
        const [itemsRes, catsRes, resortRes] = await Promise.all([
          fetch(`${apiBase}/food-items/?limit=1000`).then(r => r.ok ? r.json() : { items: [] }).catch(() => ({ items: [] })),
          fetch(`${apiBase}/food-categories/`).then(r => r.ok ? r.json() : []).catch(() => []),
          fetch(`${apiBase}/frontend/resort-info/`).then(r => r.ok ? r.json() : []).catch(() => [])
        ]);

        if (isMounted) {
          const rawItems = Array.isArray(itemsRes) ? itemsRes : (itemsRes?.items || []);
          setFoodItems(rawItems);

          const rawCats = Array.isArray(catsRes) ? catsRes : [];
          setCategories(rawCats);

          if (Array.isArray(resortRes) && resortRes.length > 0) {
            setResortInfo(resortRes[0]);
          } else if (resortRes && typeof resortRes === "object") {
            setResortInfo(resortRes);
          }
        }
      } catch (err) {
        console.error("Failed to fetch E-Menu data:", err);
        if (isMounted) setError("Could not load digital menu. Please try again shortly.");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchMenuData();
    return () => { isMounted = false; };
  }, []);

  // Image URL helper
  const getImageUrl = (imagePath) => {
    if (!imagePath) return ITEM_PLACEHOLDER;
    if (imagePath.startsWith("http")) return imagePath;
    const baseUrl = getMediaBaseUrl();
    let path = imagePath.replace(/\\/g, "/");
    if (!path.startsWith("/")) path = `/${path}`;
    if (path.startsWith("/static/uploads/")) {
      path = path.replace("/static/uploads/", "/uploads/");
    }
    return `${baseUrl}${encodeURI(path)}`;
  };

  // Group and normalize categories
  const categoryList = useMemo(() => {
    const list = ["All"];
    const dynamicNames = new Set();

    categories.forEach(c => {
      if (c && c.name) dynamicNames.add(c.name.trim());
    });

    foodItems.forEach(item => {
      const catName = item.category?.name || item.category_name;
      if (catName) dynamicNames.add(catName.trim());
    });

    return [...list, ...Array.from(dynamicNames)];
  }, [categories, foodItems]);

  // Filtered Food Items
  const filteredItems = useMemo(() => {
    return foodItems.filter(item => {
      // Category check
      if (selectedCategory !== "All") {
        const itemCat = (item.category?.name || item.category_name || "Uncategorized").trim();
        if (itemCat.toLowerCase() !== selectedCategory.toLowerCase()) {
          return false;
        }
      }

      // Available only check
      if (availableOnly && !item.available) {
        return false;
      }

      // Search query check
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const nameMatch = item.name?.toLowerCase().includes(q);
        const descMatch = item.description?.toLowerCase().includes(q);
        const catMatch = (item.category?.name || item.category_name || "").toLowerCase().includes(q);
        return nameMatch || descMatch || catMatch;
      }

      return true;
    });
  }, [foodItems, selectedCategory, availableOnly, searchQuery]);

  // Handle Share link
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: "Pomma Holidays Digital E-Menu",
        text: "Explore our culinary dining menu at Pomma Holidays.",
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(window.location.href);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    }
  };

  return (
    <div className="min-h-screen bg-[#f9f4ea] text-[#153a2c] font-sans selection:bg-[#0f5132] selection:text-white pb-20">
      
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-40 bg-[#0f5132]/95 backdrop-blur-md border-b border-[#c99c4e]/30 text-white shadow-lg transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          
          {/* Left: Brand / Return */}
          <div className="flex items-center space-x-3 sm:space-x-4">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-[#f9f4ea] text-xs sm:text-sm font-medium border border-white/20 transition-colors"
              title="Return to Resort Website"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </button>

            <div 
              onClick={() => navigate("/")} 
              className="cursor-pointer flex items-center gap-2.5 group"
            >
              <img 
                src={localLogo} 
                alt="Pomma Holidays" 
                className="h-10 sm:h-12 w-auto object-contain filter drop-shadow group-hover:scale-105 transition-transform" 
              />
              <div className="hidden md:block leading-tight">
                <span className="block text-sm font-serif font-bold tracking-wider uppercase text-[#c99c4e]">Pomma Holidays</span>
                <span className="block text-[10px] tracking-widest uppercase text-white/80">Digital E-Menu</span>
              </div>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            <button
              onClick={handleShare}
              className="p-2 sm:px-3.5 sm:py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-medium border border-white/20 flex items-center gap-1.5 transition-colors"
              title="Share E-Menu"
            >
              <Share2 className="w-4 h-4 text-[#c99c4e]" />
              <span className="hidden sm:inline">{copiedLink ? "Copied!" : "Share Menu"}</span>
            </button>
          </div>

        </div>
      </header>

      {/* Hero Banner Showcase */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#0f5132] via-[#145a39] to-[#1a7042] text-white pt-10 pb-16 px-4 sm:px-6 lg:px-8 shadow-inner">
        {/* Decorative background glow */}
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#c99c4e]/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-[#0c3d26]/40 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-[#c99c4e]/40 text-[#c99c4e] text-xs sm:text-sm font-semibold tracking-widest uppercase mb-4 shadow-sm">
            <Sparkles className="w-4 h-4 text-[#c99c4e]" />
            <span>Digital Culinary Showcase</span>
          </div>

          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight text-[#f9f4ea] mb-4 drop-shadow-md">
            Pomma Dining E-Menu
          </h1>

          <p className="text-sm sm:text-lg text-[#f9f4ea]/85 max-w-2xl mx-auto font-light leading-relaxed mb-6">
            A showcase of gourmet dishes crafted with authentic spices, fresh local ingredients, and world-class culinary finesse.
          </p>

          {/* Quick Dining Info Badges */}
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 text-xs sm:text-sm text-[#f9f4ea]/90">
            <div className="flex items-center gap-1.5 bg-black/20 backdrop-blur-sm px-3.5 py-1.5 rounded-full border border-white/10">
              <Clock className="w-4 h-4 text-[#c99c4e]" />
              <span>Dining Timings: 7:30 AM – 10:30 PM</span>
            </div>
            <div className="flex items-center gap-1.5 bg-black/20 backdrop-blur-sm px-3.5 py-1.5 rounded-full border border-white/10">
              <Utensils className="w-4 h-4 text-[#c99c4e]" />
              <span>{foodItems.length} Signature Dishes</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">

        {/* Search & Filter Bar */}
        <div className="bg-white rounded-2xl shadow-xl border border-[#e2d6c0] p-4 sm:p-5 mb-8 backdrop-blur-md">
          <div className="flex flex-col md:flex-row items-center gap-4">
            
            {/* Search Input */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4f6f62]" />
              <input
                type="text"
                placeholder="Search dishes, ingredients, or cuisines..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-10 py-3 bg-[#f9f4ea]/60 border border-[#d6c8ab] rounded-xl text-sm text-[#153a2c] placeholder-[#6e8579] focus:outline-none focus:ring-2 focus:ring-[#0f5132] focus:bg-white transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Availability Filter Toggle */}
            <div className="flex items-center justify-between w-full md:w-auto gap-3">
              <button
                type="button"
                onClick={() => setAvailableOnly(!availableOnly)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-xs sm:text-sm font-semibold transition-all ${
                  availableOnly
                    ? "bg-[#0f5132] text-white border-[#0f5132] shadow"
                    : "bg-[#f9f4ea]/60 text-[#153a2c] border-[#d6c8ab] hover:bg-[#f9f4ea]"
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${availableOnly ? "bg-emerald-400" : "bg-gray-400"}`}></span>
                <span>Available Today</span>
              </button>

              <div className="text-xs text-[#4f6f62] font-medium whitespace-nowrap pl-2">
                Showing <strong className="text-[#0f5132]">{filteredItems.length}</strong> items
              </div>
            </div>

          </div>
        </div>

        {/* Category Horizontal Pills (Sticky) */}
        <div className="sticky top-20 z-30 bg-[#f9f4ea]/95 backdrop-blur-md py-3 mb-8 border-b border-[#e2d6c0] -mx-4 px-4 sm:mx-0 sm:px-0">
          <div 
            ref={categoryScrollRef}
            className="flex items-center gap-2 overflow-x-auto no-scrollbar scroll-smooth py-1"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {categoryList.map((category) => {
              const isSelected = selectedCategory.toLowerCase() === category.toLowerCase();
              const count = category === "All"
                ? foodItems.length
                : foodItems.filter(item => (item.category?.name || item.category_name || "Uncategorized").toLowerCase() === category.toLowerCase()).length;

              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`flex-shrink-0 flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-semibold transition-all duration-200 transform active:scale-95 ${
                    isSelected
                      ? "bg-gradient-to-r from-[#0f5132] to-[#1a7042] text-white shadow-md shadow-[#0f5132]/25 scale-105"
                      : "bg-white text-[#153a2c] border border-[#d6c8ab] hover:border-[#0f5132] hover:bg-white"
                  }`}
                >
                  <span>{category}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    isSelected ? "bg-white/20 text-white" : "bg-[#0f5132]/10 text-[#0f5132]"
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content States */}
        {isLoading ? (
          <div className="py-24 text-center">
            <div className="w-14 h-14 border-4 border-[#0f5132]/20 border-t-[#0f5132] rounded-full animate-spin mx-auto mb-4"></div>
            <h3 className="text-lg font-serif font-bold text-[#153a2c]">Loading E-Menu Showcase...</h3>
            <p className="text-sm text-[#4f6f62] mt-1">Preparing dishes and culinary details</p>
          </div>
        ) : error ? (
          <div className="py-16 text-center max-w-md mx-auto bg-white rounded-2xl border border-red-200 p-8 shadow-sm">
            <p className="text-sm text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2.5 bg-[#0f5132] text-white text-sm font-semibold rounded-full shadow hover:bg-[#1a7042] transition-colors"
            >
              Reload Menu
            </button>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="py-20 text-center max-w-md mx-auto bg-white rounded-3xl border border-[#e2d6c0] p-8 shadow-sm">
            <Utensils className="w-12 h-12 text-[#c99c4e] mx-auto mb-3 opacity-80" />
            <h3 className="text-xl font-serif font-bold text-[#153a2c] mb-2">No Dishes Found</h3>
            <p className="text-sm text-[#4f6f62] mb-6">
              {searchQuery
                ? `No dishes matched "${searchQuery}". Try searching with another keyword or resetting the filter.`
                : `No items available under "${selectedCategory}" right now.`}
            </p>
            {(searchQuery || selectedCategory !== "All" || availableOnly) && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCategory("All");
                  setAvailableOnly(false);
                }}
                className="px-6 py-2.5 bg-[#0f5132] text-white text-sm font-semibold rounded-full shadow hover:bg-[#1a7042] transition-colors"
              >
                Reset Filters
              </button>
            )}
          </div>
        ) : (
          /* Food Items Grid Showcase */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredItems.map((item) => {
              const categoryName = item.category?.name || item.category_name || "Specialty";
              const primaryImage = item.images && item.images.length > 0 
                ? getImageUrl(item.images[0].image_url) 
                : ITEM_PLACEHOLDER;

              return (
                <div
                  key={item.id}
                  onClick={() => {
                    setSelectedItem(item);
                    setActiveImageIndex(0);
                  }}
                  className="group bg-white rounded-2xl overflow-hidden border border-[#e2d6c0] hover:border-[#c99c4e] shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1.5 flex flex-col cursor-pointer"
                >
                  {/* Food Image */}
                  <div className="relative h-48 sm:h-52 w-full overflow-hidden bg-neutral-100">
                    <ProgressiveImage
                      src={primaryImage}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-700 ease-out"
                    />
                    
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 opacity-70 group-hover:opacity-85 transition-opacity"></div>

                    {/* Category Badge */}
                    <div className="absolute top-3 left-3">
                      <span className="px-3 py-1 rounded-full text-[11px] font-semibold bg-white/90 backdrop-blur-md text-[#0f5132] shadow-sm uppercase tracking-wider">
                        {categoryName}
                      </span>
                    </div>

                    {/* Availability Pill */}
                    <div className="absolute top-3 right-3">
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold shadow-sm backdrop-blur-md ${
                        item.available
                          ? "bg-emerald-600/90 text-white"
                          : "bg-rose-600/90 text-white"
                      }`}>
                        {item.available ? "Available" : "Sold Out"}
                      </span>
                    </div>

                    {/* Price Tag Overlay at Bottom */}
                    <div className="absolute bottom-3 right-3">
                      <div className="px-3 py-1.5 rounded-xl bg-black/75 backdrop-blur-md text-[#f9f4ea] font-serif font-bold text-base shadow-lg border border-[#c99c4e]/50">
                        {formatCurrency(item.price)}
                      </div>
                    </div>

                    {/* Photo count indicator */}
                    {item.images && item.images.length > 1 && (
                      <div className="absolute bottom-3 left-3 text-[10px] bg-black/60 text-white/90 px-2 py-0.5 rounded-md backdrop-blur-sm">
                        +{item.images.length} photos
                      </div>
                    )}
                  </div>

                  {/* Food Content Details */}
                  <div className="p-5 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="font-serif font-bold text-lg text-[#153a2c] group-hover:text-[#0f5132] transition-colors leading-snug mb-1.5">
                        {item.name}
                      </h3>

                      <p className="text-xs text-[#4f6f62] line-clamp-2 leading-relaxed mb-4">
                        {item.description || "Freshly crafted specialty prepared with authentic ingredients by our master chefs."}
                      </p>
                    </div>

                    {/* Bottom Details Link */}
                    <div className="pt-3 border-t border-[#f1e7d8] flex items-center justify-between text-xs">
                      <span className="text-[#c99c4e] font-semibold uppercase tracking-wider text-[11px] flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                        <span>View Details</span>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </span>

                      <span className="text-xs font-semibold text-[#0f5132]">
                        {formatCurrency(item.price)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Resort Dining Guidelines Section */}
        <section className="mt-16 bg-gradient-to-br from-[#0f5132] to-[#1a7042] text-white rounded-3xl p-6 sm:p-10 shadow-xl border border-[#c99c4e]/30 relative overflow-hidden">
          <div className="absolute -right-10 -bottom-10 w-64 h-64 bg-[#c99c4e]/10 rounded-full blur-2xl pointer-events-none"></div>

          <div className="max-w-3xl relative z-10">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-white/10 text-[#c99c4e] text-xs font-semibold uppercase tracking-wider mb-3">
              <Info className="w-3.5 h-3.5" />
              <span>Dining Information</span>
            </div>

            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-[#f9f4ea] mb-3">
              Resort Restaurant & Dining Hours
            </h2>

            <p className="text-xs sm:text-sm text-white/80 leading-relaxed mb-6">
              Our culinary offerings are crafted fresh with organic spices and high quality produce. Items on our menu may vary depending on seasonal ingredient availability.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-white/20 text-center sm:text-left">
              <div>
                <span className="block text-xs uppercase tracking-wider text-[#c99c4e] font-semibold">Breakfast</span>
                <span className="text-sm font-bold text-white">7:30 AM – 10:30 AM</span>
              </div>
              <div>
                <span className="block text-xs uppercase tracking-wider text-[#c99c4e] font-semibold">Lunch</span>
                <span className="text-sm font-bold text-white">12:30 PM – 3:30 PM</span>
              </div>
              <div>
                <span className="block text-xs uppercase tracking-wider text-[#c99c4e] font-semibold">Snacks & Tea</span>
                <span className="text-sm font-bold text-white">4:30 PM – 6:30 PM</span>
              </div>
              <div>
                <span className="block text-xs uppercase tracking-wider text-[#c99c4e] font-semibold">Dinner</span>
                <span className="text-sm font-bold text-white">7:30 PM – 10:30 PM</span>
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* Enlarged Dish Details Modal */}
      {selectedItem && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 animate-fadeIn"
          onClick={() => setSelectedItem(null)}
        >
          <div 
            className="bg-white rounded-3xl max-w-2xl w-full overflow-hidden shadow-2xl border border-[#e2d6c0] flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Image Carousel */}
            <div className="relative h-64 sm:h-80 w-full bg-black overflow-hidden flex-shrink-0">
              {(() => {
                const images = selectedItem.images && selectedItem.images.length > 0 
                  ? selectedItem.images 
                  : [{ image_url: "" }];
                const currentImg = images[activeImageIndex] || images[0];

                return (
                  <>
                    <img
                      src={getImageUrl(currentImg.image_url)}
                      alt={selectedItem.name}
                      className="w-full h-full object-cover"
                    />

                    {/* Close Button */}
                    <button
                      onClick={() => setSelectedItem(null)}
                      className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/60 hover:bg-black/90 text-white flex items-center justify-center transition-colors shadow-lg z-20"
                    >
                      <X className="w-5 h-5" />
                    </button>

                    {/* Navigation Arrows if Multiple Images */}
                    {images.length > 1 && (
                      <>
                        <button
                          onClick={() => setActiveImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))}
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 hover:bg-black/80 text-white flex items-center justify-center transition-colors z-20"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setActiveImageIndex((prev) => (prev + 1) % images.length)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 hover:bg-black/80 text-white flex items-center justify-center transition-colors z-20"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>

                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-20 bg-black/40 px-3 py-1.5 rounded-full backdrop-blur-sm">
                          {images.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={() => setActiveImageIndex(idx)}
                              className={`w-2 h-2 rounded-full transition-all ${
                                idx === activeImageIndex ? "bg-[#c99c4e] w-4" : "bg-white/60"
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Modal Content */}
            <div className="p-6 sm:p-8 overflow-y-auto space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#0f5132]/10 text-[#0f5132] inline-block mb-2">
                    {selectedItem.category?.name || selectedItem.category_name || "Specialty"}
                  </span>
                  <h2 className="text-2xl sm:text-3xl font-serif font-bold text-[#153a2c]">
                    {selectedItem.name}
                  </h2>
                </div>

                <div className="text-right">
                  <div className="text-2xl font-serif font-bold text-[#0f5132]">
                    {formatCurrency(selectedItem.price)}
                  </div>
                  <span className={`inline-block mt-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                    selectedItem.available ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"
                  }`}>
                    {selectedItem.available ? "Available" : "Sold Out"}
                  </span>
                </div>
              </div>

              {/* Description */}
              <div className="py-2 border-t border-b border-gray-100 text-sm text-[#4f6f62] leading-relaxed">
                <p>{selectedItem.description || "Crafted fresh upon order with authentic regional culinary craftsmanship."}</p>
              </div>

              <div className="pt-2 flex justify-end">
                <button
                  onClick={() => setSelectedItem(null)}
                  className="px-6 py-2.5 bg-[#0f5132] hover:bg-[#1a7042] text-white font-semibold text-sm rounded-full transition-colors shadow"
                >
                  Close
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="mt-20 border-t border-[#e2d6c0] bg-white py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left text-xs text-[#4f6f62]">
          <div className="flex items-center gap-3">
            <img src={localLogo} alt="Pomma Holidays" className="h-8 w-auto object-contain" />
            <div>
              <p className="font-bold text-[#153a2c]">Pomma Holidays & Resorts</p>
              <p>{resortInfo?.address || "Wayanad, Kerala"}</p>
            </div>
          </div>

          <div className="flex space-x-4 text-[#0f5132]">
            {resortInfo?.facebook && (
              <a href={resortInfo.facebook} target="_blank" rel="noopener noreferrer" className="hover:text-[#c99c4e] transition-colors"><Facebook className="w-4 h-4" /></a>
            )}
            {resortInfo?.instagram && (
              <a href={resortInfo.instagram} target="_blank" rel="noopener noreferrer" className="hover:text-[#c99c4e] transition-colors"><Instagram className="w-4 h-4" /></a>
            )}
            {resortInfo?.twitter && (
              <a href={resortInfo.twitter} target="_blank" rel="noopener noreferrer" className="hover:text-[#c99c4e] transition-colors"><Twitter className="w-4 h-4" /></a>
            )}
            {resortInfo?.linkedin && (
              <a href={resortInfo.linkedin} target="_blank" rel="noopener noreferrer" className="hover:text-[#c99c4e] transition-colors"><Linkedin className="w-4 h-4" /></a>
            )}
          </div>

          <p>© {new Date().getFullYear()} Pomma Holidays. All Rights Reserved.</p>
        </div>
      </footer>

    </div>
  );
}
